// src/components/Seo.jsx
// 프리렌더(vite-react-ssg) 각 페이지의 <head> SEO 태그를 채우는 재사용 컴포넌트.
// <Head> 는 react-helmet-async 재export 로, HelmetProvider 는 vite-react-ssg 가
// 이미 감싸므로 여기서 추가하지 않는다. 프리렌더 산출물에서는 index.html 의
// 정적 <title> 등을 이 값이 덮어쓴다.
import { Head } from 'vite-react-ssg';

const SITE_URL = 'https://medilawyer.co.kr';

// 대표 이미지가 없는 페이지의 기본 OG 이미지.
// index.html 에 정적으로 두면 helmet 이 덮지 못해 태그가 두 개가 된다(파비콘이 이긴다).
// → 정적 태그는 걷어내고 여기서 항상 하나만 내보낸다.
const DEFAULT_OG_IMAGE = `${SITE_URL}/favicon-512.png`;

// JSON-LD 를 안전하게 직렬화한다. "<" 를 유니코드 이스케이프로 치환해
// 본문에 "</script>" 가 들어가도 스크립트 태그가 조기 종료되지 않도록 한다(XSS/파싱 방어).
function serializeJsonLd(node) {
  return JSON.stringify(node).replace(/</g, '\\u003c');
}

/**
 * @param {Object} props
 * @param {string} props.title       페이지별 <title> (예: "환자 클레임 대응 — medilawyer")
 * @param {string} props.description meta description / og:description
 * @param {string} props.path        라우트 경로 (예: "/coverage/patient-claim")
 * @param {boolean} [props.noindex]  true 면 robots noindex (ComingSoon 등)
 * @param {string} [props.image]     페이지 대표 이미지 절대 URL (예: 블로그 글 썸네일).
 *   없으면 사이트 기본 이미지(파비콘)를 쓴다.
 * @param {object|object[]} [props.jsonLd] schema.org JSON-LD 객체 또는 객체 배열.
 *   각 항목은 <script type="application/ld+json"> 으로 head 에 주입된다.
 */
export function Seo({ title, description, path, noindex = false, image, jsonLd }) {
  const canonical = `${SITE_URL}${path}`;
  const ogImage = image || DEFAULT_OG_IMAGE;
  // 글 대표 이미지가 있으면 큰 카드, 없으면(파비콘) 작은 카드.
  const twitterCard = image ? 'summary_large_image' : 'summary';
  // 단일 객체도 배열도 받아 일관되게 순회한다.
  const jsonLdNodes = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  return (
    <Head>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:image" content={ogImage} />
      {noindex && <meta name="robots" content="noindex, follow" />}
      {jsonLdNodes.map((node, index) => (
        // react-helmet-async 는 <script> 의 자식(children) 텍스트를 innerHTML 로
        // 취급해 SSR 시 이스케이프 없이 그대로 출력한다. dangerouslySetInnerHTML 를
        // 쓰면 helmet 이 innerHTML 로 인식하지 못해 script 가 통째로 누락된다
        // (helmet 은 src 또는 innerHTML 이 있는 script 만 직렬화). 따라서 자식 텍스트로 넘긴다.
        // serializeJsonLd 가 "<" 를 이스케이프하므로 "</script>" 조기 종료는 막힌다.
        <script key={index} type="application/ld+json">
          {serializeJsonLd(node)}
        </script>
      ))}
    </Head>
  );
}
